/**
 * Environment (RB §8).
 *
 * Two tables here are **partitions of 1–20**, and both are asserted as partitions
 * in `data-tables.test.ts` rather than spot-checked. That is deliberate: RB §8.3 as
 * written overlaps at 5 ("3–5 restrictive, 5–20 clear"), errata #5 fixes it to
 * 6–20, and the way to make sure such a fix stays fixed is a test that fails on any
 * gap or overlap rather than a comment saying it was fixed.
 */
// ---------------------------------------------------------------------------
// Debris (RB §8.1)
// ---------------------------------------------------------------------------
/** RB §8.1. Enforced in `applyMutation`; restated here so the table is diffable. */
export const MAX_DEBRIS = 5;
/** Errata #6: further debris beyond the cap is discarded, not banked. */
export const DEBRIS_ABOVE_CAP_IS_DISCARDED = true;
export const CONJUNCTION_SEVERITY = Object.freeze([
    { from: 1, to: 3, result: 'CATASTROPHIC', summary: 'DESTROYED, and generates debris.' },
    { from: 4, to: 12, result: 'IMPACT', summary: 'Impact damage: DEGRADED, permanently (RB §1.1).' },
    {
        from: 13,
        to: 20,
        result: 'NEAR_MISS',
        summary: 'Expends 15 m/s. An asset that cannot pay takes the impact-damage result instead.',
    },
]);
export function conjunctionResult(roll) {
    for (const row of CONJUNCTION_SEVERITY) {
        if (roll >= row.from && roll <= row.to)
            return row.result;
    }
    // Unreachable while the partition test passes, and a loud default if it ever
    // does not — silently treating an out-of-range roll as a near miss would hide
    // the table defect behind a merciful outcome.
    return 'IMPACT';
}
/** RB §8.2.2's near-miss cost. Insufficient delta-V downgrades to impact damage. */
export const CONJUNCTION_NEAR_MISS_DELTAV = 15;
/** RB §8.2.2 modifiers, applied to the *avoidance* roll. */
export const CONJUNCTION_DEBRIS_SURVEY_BONUS = 2;
export const CONJUNCTION_MANEUVERED_PENALTY = -2;
export const CONJUNCTION_DEGRADED_PENALTY = -2;
export const WEATHER_TABLE = Object.freeze([
    {
        from: 1,
        to: 2,
        weather: 'PROHIBITIVE',
        summary: 'Ground optical sites and EO ISR against the region automatically fail.',
    },
    {
        from: 3,
        to: 5,
        weather: 'RESTRICTIVE',
        summary: 'Ground optical sites and EO ISR against the region take [-4].',
    },
    // Errata #5: RB §8.3 says "5–20 clear", overlapping the row above. 6–20.
    { from: 6, to: 20, weather: 'CLEAR', summary: 'No penalty.' },
]);
export function weatherFor(roll) {
    for (const row of WEATHER_TABLE) {
        if (roll >= row.from && roll <= row.to)
            return row.weather;
    }
    return 'CLEAR';
}
/** RB §8.3 / §5.8.3 — the EO penalty under restrictive weather. */
export const RESTRICTIVE_WEATHER_MODIFIER = -4;
export const ENVIRONMENT_TABLE_MANIFEST = {
    MAX_DEBRIS,
    CONJUNCTION_SEVERITY,
    CONJUNCTION_NEAR_MISS_DELTAV,
    CONJUNCTION_DEBRIS_SURVEY_BONUS,
    CONJUNCTION_MANEUVERED_PENALTY,
    CONJUNCTION_DEGRADED_PENALTY,
    WEATHER_TABLE,
    RESTRICTIVE_WEATHER_MODIFIER,
};
//# sourceMappingURL=environment.js.map